import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import CustomModal from "../../../components/modal/CustomModal";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import CustomSelect, { MenuData } from "../../../components/input/CustomSelect";
import CustomDatePicker from "../../../components/input/CustomDatePicker";
import { common, financedEmissions } from "../../../i18n";
import { FinancialYear } from "../../../models/dto/cat15/cat15.types";

export interface FinancialDataForm {
  year: string;
  /** figures are in raw baht (THB) — matching the Financial table; sent to the API as-is */
  evic: string;
  equity: string;
  debt: string;
  revenue: string;
  /** YYYY-MM-DD, optional */
  asOfDate: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  borrowerName?: string;
  /** years already present — excluded from the Add picker (BE 409s on a dup) */
  existingYears: number[];
  /** present = edit mode: year is locked and the figures prefilled */
  editRow?: FinancialYear | null;
  onSubmit: (values: FinancialDataForm, editYear: number | null) => void;
}

/** How many years back the Add picker offers. */
const YEAR_SPAN = 7;

/** Add / Edit a borrower's bank-entered financial figures (Company Value). */
const FinancialDataModal = ({
  open,
  onClose,
  borrowerName,
  existingYears,
  editRow,
  onSubmit,
}: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const isEdit = !!editRow;

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<FinancialDataForm>({
      defaultValues: { year: "", evic: "", equity: "", debt: "", revenue: "", asOfDate: "" },
    });
  const [asOf, setAsOf] = useState<Dayjs | null>(null);

  // Years not already entered, newest first (Add mode only).
  const yearOptions: MenuData[] = useMemo(() => {
    const taken = new Set(existingYears);
    const thisYear = new Date().getFullYear();
    return Array.from({ length: YEAR_SPAN }, (_, i) => thisYear - i)
      .filter((y) => !taken.has(y))
      .map((y) => ({ value: String(y), nameEN: String(y), nameTH: String(y) }));
  }, [existingYears]);

  // (Re)seed the form whenever the modal opens — prefill in edit mode.
  useEffect(() => {
    if (!open) return;
    if (editRow) {
      const num = (v: number) => (v ? String(v) : "");
      reset({
        year: String(editRow.year),
        evic: num(editRow.evic),
        equity: num(editRow.equity),
        debt: num(editRow.debt),
        revenue: num(editRow.revenue),
        asOfDate: editRow.asOfDate ?? "",
      });
      setAsOf(editRow.asOfDate ? dayjs(editRow.asOfDate) : null);
    } else {
      reset({ year: "", evic: "", equity: "", debt: "", revenue: "", asOfDate: "" });
      setAsOf(null);
    }
  }, [open, editRow, reset]);

  const year = watch("year");
  const [evic, equity, debt, revenue] = watch(["evic", "equity", "debt", "revenue"]);
  // BE rejects a row with no figures — require at least one.
  const hasFigure = [evic, equity, debt, revenue].some((v) => v.trim() !== "");
  const disabled = !year || !hasFigure;

  const submit = (values: FinancialDataForm) => {
    onSubmit(
      { ...values, asOfDate: asOf ? asOf.format("YYYY-MM-DD") : "" },
      isEdit ? Number(editRow?.year) : null,
    );
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      maxWidth="588px"
      header={
        <Box>
          <Typography variant="h1">
            {isEdit ? t("Edit-Financial-Year") : `+ ${t("Add-Financial-Year")}`}
          </Typography>
          {borrowerName && (
            <Typography variant="body2" color="var(--gray9)">
              {borrowerName}
            </Typography>
          )}
        </Box>
      }
      body={
        <Box display="flex" flexDirection="column" gap="20px">
          <CustomSelect
            required
            label={t("Col-Year")}
            value={year}
            placeholder={t("Select-Year")}
            disabled={isEdit}
            listMenuItem={
              isEdit
                ? [{ value: year, nameEN: year, nameTH: year }]
                : yearOptions
            }
            handleValeOnChange={(e) => setValue("year", e.target.value)}
          />

          <Grid container spacing="20px">
            <Grid xs={12} sm={6}>
              <InputReactHookForm name="evic" control={control} label={t("Col-EVIC")} placeholder="0.00" />
            </Grid>
            <Grid xs={12} sm={6}>
              <InputReactHookForm name="revenue" control={control} label={t("Col-Revenue")} placeholder="0.00" />
            </Grid>
            <Grid xs={12} sm={6}>
              <InputReactHookForm name="equity" control={control} label={t("Col-Equity-M")} placeholder="0.00" />
            </Grid>
            <Grid xs={12} sm={6}>
              <InputReactHookForm name="debt" control={control} label={t("Col-Debt-M")} placeholder="0.00" />
            </Grid>
          </Grid>

          <CustomDatePicker
            label={t("Col-As-Of")}
            info={t("Col-As-Of")}
            views={["year", "month", "day"]}
            format="DD/MM/YYYY"
            value={asOf}
            defaultDate={null}
            disableFuture
            handleOnChange={(d: Dayjs | null) => setAsOf(d ? dayjs(d) : null)}
          />

          <Typography variant="body2" color="var(--gray1)">
            {t("Financial-One-Required")}
          </Typography>

          <Box display="flex" justifyContent="flex-end" gap="12px">
            <Button variant="outlined" color="secondary" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="green"
              disabled={disabled}
              onClick={handleSubmit(submit)}
            >
              {t("Save")}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

export default FinancialDataModal;
